import React, { useState, useEffect } from 'react';
import { AlertTriangle, XCircle, X, ArrowRight } from 'lucide-react';
import { formatCurrency, formatUnitPrice } from '../../utils/formatting';
import { apiService } from '../../services/apiService';

// Fallback UOM mapping — used until dynamic map is loaded from backend
const FALLBACK_UOM_CANONICAL = {
  'RL': 'RL', 'ROL': 'RL', 'ROLL': 'RL',
  'BX': 'BX', 'BOX': 'BX',
  'EA': 'EA', 'EACH': 'EA', 'PC': 'EA', 'PCS': 'EA', 'UN': 'EA', 'UNIT': 'EA',
  'CS': 'CS', 'CASE': 'CS', 'CA': 'CS',
  'CT': 'CT', 'CTN': 'CT', 'CARTON': 'CT',
  'PK': 'PK', 'PKG': 'PK', 'PACK': 'PK', 'PACKAGE': 'PK',
  'DZ': 'DZ', 'DOZ': 'DZ', 'DOZEN': 'DZ',
  'FT': 'FT', 'FOOT': 'FT', 'FEET': 'FT',
  'IN': 'IN', 'INCH': 'IN', 'INCHES': 'IN',
  'LB': 'LB', 'LBS': 'LB', 'POUND': 'LB', 'POUNDS': 'LB',
  'OZ': 'OZ', 'OUNCE': 'OZ', 'OUNCES': 'OZ',
  'GAL': 'GAL', 'GALLON': 'GAL', 'GALLONS': 'GAL',
  'QT': 'QT', 'QUART': 'QT', 'QUARTS': 'QT',
  'PT': 'PT', 'PINT': 'PT', 'PINTS': 'PT',
  'M': 'M', 'THOUSAND': 'M', '1000 EA': 'M',
};

// Module-level caches
let _cachedUomMap = null;
const _cachedItemUoms = {}; // keyed by item_number

function normalizeUom(uom, uomMap) {
  if (!uom) return '';
  const upper = uom.trim().toUpperCase();
  return (uomMap || FALLBACK_UOM_CANONICAL)[upper] || upper;
}

// Known universal UOM conversion factors: key = "FROM_TO" (canonical), value = how many FROM per 1 TO
const KNOWN_UOM_CONVERSIONS = {
  'EA_M': 1000,    // 1000 EA = 1 M (per thousand)
  'EA_DZ': 12,     // 12 EA = 1 DZ (dozen)
  'EA_GR': 144,    // 144 EA = 1 GR (gross)
};

function getKnownConversion(fromUom, toUom, uomMap) {
  const key = `${normalizeUom(fromUom, uomMap)}_${normalizeUom(toUom, uomMap)}`;
  return KNOWN_UOM_CONVERSIONS[key] || null;
}

function isNearInteger(n) {
  return n > 0 && Math.abs(n - Math.round(n)) < 0.01;
}

export default function PriceResolutionModal({ item, itemIndex, mode, onApply, onClose }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [uomMap, setUomMap] = useState(_cachedUomMap || FALLBACK_UOM_CANONICAL);
  const [itemUoms, setItemUoms] = useState(_cachedItemUoms[item.selectedItemNumber] || null);

  // Load dynamic UOM map from backend (once, cached at module level)
  useEffect(() => {
    if (_cachedUomMap) return;
    apiService.getUomMap()
      .then(data => {
        _cachedUomMap = data.map;
        setUomMap(data.map);
      })
      .catch(() => {}); // Fallback already set
  }, []);

  // Load A+-valid UOMs for this item (lazy: fetch once per item, cached)
  useEffect(() => {
    const itemNum = item.selectedItemNumber;
    if (!itemNum || _cachedItemUoms[itemNum]) return;
    apiService.getItemUoms(itemNum)
      .then(data => {
        _cachedItemUoms[itemNum] = data.uoms || [];
        setItemUoms(data.uoms || []);
      })
      .catch(() => {}); // Fall back to uomOptions derived from PO/ERP/stock UOMs
  }, [item.selectedItemNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract PO item data for unit price
  const poUnitPrice = item.extUnitPrice;

  const poUom = item.poUnitOfMeasure || '';
  const erpUom = item.valPriceUom || '';
  const stockUom = item.unitOfMeasure || '';
  const hasContractPricing = item.valUnitPrice != null;
  const stockResolved = item.priceStockUomResolved || false;

  // Stocking UOMs from item master
  const stockingUom1 = item.unitOfMeasure || '';  // IMUNM1 (always populated)
  const stockingUom2Raw = item.stockingUom2 || '';
  const stockingUom3Raw = item.stockingUom3 || '';
  const convFactor1 = item.uomConversion1;  // IMUMC1: how many IMUNM2 per IMUNM1
  const convFactor2 = item.uomConversion2;  // IMUMC2: how many IMUNM3 per IMUNM1

  // Build list of valid stocking UOMs (only populated ones)
  const validStockingUoms = [stockingUom1, stockingUom2Raw, stockingUom3Raw].filter(u => u && u.trim());

  // Is the PO UOM a valid stocking UOM?
  const poUomIsValidStocking = validStockingUoms.some(su => normalizeUom(su, uomMap) === normalizeUom(poUom, uomMap));

  // When stock UOM conversion is active, use stock-converted values for display
  const effectiveContractPrice = stockResolved && item.priceStockUnitPrice != null
    ? item.priceStockUnitPrice : item.valUnitPrice;
  const effectiveContractUom = stockResolved ? poUom : erpUom;

  const isUomMode = mode === 'uom_mismatch';
  // Manual-price mode: opened for CSR-added lines with no resolved price (and no A+ contract
  // price to auto-resolve). Offers only the Custom entry (+ Contract if one happens to exist);
  // the PO option is hidden because manually-added lines have no PO data.
  const isManualMode = mode === 'manual_price';

  // UOM options for the custom dropdown.
  // In UOM-mismatch mode the resolution MUST land on a valid stocking unit — A+ submits the
  // line in a stocking unit (IMUNM1/2/3), and the review screen's "not a valid stocking unit"
  // guard rejects anything else. So offer only the stocking units here. Previously we offered
  // the full A+ UOM list (/api/itemUoms), which includes the pricing unit and alternates (e.g.
  // RL/EA for a CS-stocked item) — the CSR could pick one, then get bounced with "re-resolve to
  // CS," an offer-then-reject loop (Tonya, 2026-07-24). Outside UOM-mismatch mode (price
  // variance / manual price) the UOM isn't the thing being resolved, so keep the full list.
  // Codes are canonicalized so the seeded UOM below always matches a rendered option.
  const uomOptions = isUomMode && validStockingUoms.length > 0
    ? [...new Set(validStockingUoms.map(u => normalizeUom(u, uomMap)))]
    : itemUoms && itemUoms.length > 0
      ? itemUoms.map(u => u.code)
      : [...new Set([poUom, erpUom, stockUom].filter(Boolean).map(u => normalizeUom(u, uomMap)))];

  // Convert a (qty, price) line between two UOMs using the item's conversion factors
  // (A+ IMAUM alternates via /api/itemUoms first — these include units ITMST misses,
  // e.g. EA for a CS-stocked item like 09-29365 — then ITMST IMUMC factors, then known
  // universal conversions), preserving the extended total. Returns null when no factor
  // is known, so callers leave the values untouched rather than guess.
  function convertLineUom(fromUom, toUom, qty, price) {
    const nFrom = normalizeUom(fromUom, uomMap);
    const nTo = normalizeUom(toUom, uomMap);
    const parsedPrice = (price === '' || price == null) ? null : parseFloat(price);
    if (!nFrom || !nTo) return null;
    if (nFrom === nTo) return { qty, price: parsedPrice };

    // factor = how many fromUom per 1 toUom  (toQty = fromQty / factor, toPrice = fromPrice * factor)
    let factor = null;
    if (itemUoms && itemUoms.length > 0) {
      const fFrom = itemUoms.find(u => normalizeUom(u.code, uomMap) === nFrom)?.conversion_factor;
      const fTo = itemUoms.find(u => normalizeUom(u.code, uomMap) === nTo)?.conversion_factor;
      if (fFrom && fTo && fFrom > 0 && fTo > 0) factor = fFrom / fTo;
    }
    if (!factor) {
      const n1 = normalizeUom(stockingUom1, uomMap);
      const n2 = normalizeUom(stockingUom2Raw, uomMap);
      const n3 = normalizeUom(stockingUom3Raw, uomMap);
      if (nFrom === n2 && nTo === n1 && convFactor1 > 0) factor = convFactor1;
      else if (nFrom === n1 && nTo === n2 && convFactor1 > 0) factor = 1 / convFactor1;
      else if (nFrom === n3 && nTo === n1 && convFactor2 > 0) factor = convFactor2;
      else if (nFrom === n1 && nTo === n3 && convFactor2 > 0) factor = 1 / convFactor2;
      else if (nFrom === n2 && nTo === n3 && convFactor1 > 0 && convFactor2 > 0) factor = convFactor1 / convFactor2;
      else if (nFrom === n3 && nTo === n2 && convFactor1 > 0 && convFactor2 > 0) factor = convFactor2 / convFactor1;
    }
    if (!factor) {
      const known = getKnownConversion(fromUom, toUom, uomMap);
      if (known) factor = known;
      else {
        const knownRev = getKnownConversion(toUom, fromUom, uomMap);
        if (knownRev) factor = 1 / knownRev;
      }
    }
    if (!factor || factor <= 0) return null;
    const round5 = n => Math.round(n * 100000) / 100000;
    return {
      qty: (qty != null && qty !== '') ? round5(parseFloat(qty) / factor) : qty,
      price: parsedPrice != null ? round5(parsedPrice * factor) : null,
    };
  }

  // Custom form state. Seed to a CONSISTENT triple: in UOM-mismatch mode start from the
  // PO line as-is (qty + UOM + unit price all from the PO), so the CSR never sees an EA
  // quantity paired with a CS price. Changing the UOM (handler below) re-converts both the
  // qty and the price so the line total stays correct instead of silently overcharging.
  const seedFromPo = isUomMode && item.poQuantity > 0 && poUnitPrice > 0 && poUom;
  // Seed the canonical code (e.g. "EACH" → "EA"), AND make sure the seeded UOM is actually one
  // of the selectable options. If it isn't — e.g. the PO unit EA is not a valid stocking unit
  // for an item stocked in CS/RL — the <select> would show its first option while state stayed
  // on the unselectable seed, so picking that already-displayed option fires no change event and
  // the value never updates (Tonya 2026-07-24: "selecting RL but it stays EA", then stuck on the
  // "invalid stocking unit" attention). Fall back to the first valid option and convert the
  // qty/price to match so the line total stays correct.
  const seededUom = normalizeUom(seedFromPo ? poUom : (effectiveContractUom || stockUom || poUom), uomMap);
  const initialUom = uomOptions.includes(seededUom) ? seededUom : (uomOptions[0] ?? seededUom);
  const seedQty = item.poQuantity || 1;
  const seedPrice = seedFromPo ? poUnitPrice : (effectiveContractPrice ?? '');
  const initialConv = (initialUom !== seededUom && seedPrice !== '')
    ? convertLineUom(seededUom, initialUom, seedQty, seedPrice)
    : null;
  // Switched off the seed unit but no conversion factor is known → don't carry the seed's
  // now-unit-mismatched price; blank it so the CSR must enter it (Apply stays disabled until
  // then) instead of a wrong price being applied for the new unit.
  const switchedWithoutConv = initialUom !== seededUom && !initialConv;
  const [customQty, setCustomQty] = useState(initialConv?.qty ?? seedQty);
  const [customUom, setCustomUom] = useState(initialUom);
  const [customPrice, setCustomPrice] = useState(switchedWithoutConv ? '' : (initialConv?.price ?? seedPrice));

  // Changing the UOM in the custom form re-converts qty + price to keep the line total
  // constant (e.g. 384 EA @ $9.08 <-> 96 CS @ $36.32). If no conversion factor is known,
  // the unit is changed but qty/price are left for the CSR to set manually.
  const handleCustomUomChange = newUom => {
    const conv = convertLineUom(customUom, newUom, customQty, customPrice);
    if (conv) {
      if (conv.qty != null) setCustomQty(conv.qty);
      if (conv.price != null) setCustomPrice(conv.price);
    }
    setCustomUom(newUom);
  };

  const customTotal = customQty && customPrice ? customQty * parseFloat(customPrice) : null;

  let inferredConversion = null;

  if (isUomMode && poUnitPrice > 0 && item.valUnitPrice > 0 && item.poQuantity > 0) {
    const poTotal = item.poQuantity * poUnitPrice;
    const inferredPriceQty = poTotal / item.valUnitPrice;
    const inferredFactor = item.poQuantity / inferredPriceQty;

    if (isNearInteger(inferredPriceQty) && isNearInteger(inferredFactor)) {
      inferredConversion = {
        priceQty: Math.round(inferredPriceQty),
        factor: Math.round(inferredFactor),
        label: `${Math.round(inferredFactor)}${normalizeUom(poUom, uomMap)}/${normalizeUom(erpUom, uomMap)}`,
      };
    }
  }

  // Fallback: check known UOM relationships
  if (isUomMode && !inferredConversion) {
    const knownFactor = getKnownConversion(poUom, erpUom, uomMap);
    if (knownFactor && item.poQuantity > 0) {
      const convertedQty = item.poQuantity / knownFactor;
      if (isNearInteger(convertedQty)) {
        inferredConversion = {
          priceQty: Math.round(convertedQty),
          factor: knownFactor,
          label: `${knownFactor}${normalizeUom(poUom, uomMap)}/${normalizeUom(erpUom, uomMap)}`,
        };
      }
    }
  }

  // Stock UOM conversion — when no contract pricing, convert PO values to stock UOM
  let stockConversion = null;
  if (isUomMode && !hasContractPricing && poUnitPrice > 0 && item.poQuantity > 0 && stockUom) {
    // 1. Try known universal conversions (EA→M, EA→DZ, EA→GR)
    let conversionFactor = getKnownConversion(poUom, stockUom, uomMap);

    // 2. Fallback: use item master conversion if PO UOM matches the item's pricing UOM
    if (!conversionFactor && item.valPriceUomConversion && item.valPriceUom) {
      if (normalizeUom(poUom, uomMap) === normalizeUom(item.valPriceUom, uomMap)) {
        conversionFactor = item.valPriceUomConversion;
      }
    }

    if (conversionFactor) {
      const convertedQty = item.poQuantity / conversionFactor;
      if (isNearInteger(convertedQty)) {
        stockConversion = {
          qty: Math.round(convertedQty),
          uom: stockUom,
          unitPrice: Math.round(poUnitPrice * conversionFactor * 100000) / 100000, // 5 decimal precision
          factor: conversionFactor,
          label: `${conversionFactor}${normalizeUom(poUom, uomMap)}/${normalizeUom(stockUom, uomMap)}`,
        };
      }
    }
  }

  // Build conversion options for each stocking UOM
  function buildStockingUomOptions() {
    // Only a quantity is required to offer UOM conversions. When the PO line has no
    // unit price we still convert the qty (price stays 0, resolved via the contract/
    // custom options) — previously this returned [] and left the CSR with no way to
    // convert an alt-UOM line that had no price.
    if (!item.poQuantity) return [];

    const sourceUom = poUom;
    const sourceQty = item.poQuantity;
    const sourcePrice = poUnitPrice > 0 ? poUnitPrice : 0;
    const options = [];

    for (const targetUom of validStockingUoms) {
      const normalizedTarget = normalizeUom(targetUom, uomMap);
      const normalizedSource = normalizeUom(sourceUom, uomMap);

      if (normalizedSource === normalizedTarget) {
        options.push({ uom: targetUom, qty: sourceQty, unitPrice: sourcePrice, label: null, isIdentity: true });
        continue;
      }

      let factor = null;
      const normalizedUom1 = normalizeUom(stockingUom1, uomMap);
      const normalizedUom2 = normalizeUom(stockingUom2Raw, uomMap);
      const normalizedUom3 = normalizeUom(stockingUom3Raw, uomMap);

      if (normalizedSource === normalizedUom2 && normalizedTarget === normalizedUom1 && convFactor1 && convFactor1 > 0) {
        factor = convFactor1;
      } else if (normalizedSource === normalizedUom1 && normalizedTarget === normalizedUom2 && convFactor1 && convFactor1 > 0) {
        factor = 1 / convFactor1;
      } else if (normalizedSource === normalizedUom3 && normalizedTarget === normalizedUom1 && convFactor2 && convFactor2 > 0) {
        factor = convFactor2;
      } else if (normalizedSource === normalizedUom1 && normalizedTarget === normalizedUom3 && convFactor2 && convFactor2 > 0) {
        factor = 1 / convFactor2;
      } else if (normalizedSource === normalizedUom2 && normalizedTarget === normalizedUom3 && convFactor1 && convFactor2) {
        factor = convFactor1 / convFactor2;
      } else if (normalizedSource === normalizedUom3 && normalizedTarget === normalizedUom2 && convFactor1 && convFactor2) {
        factor = convFactor2 / convFactor1;
      }

      // A+ item-master UOMs (via /api/itemUoms) include IMAUM alternates that ITMST's
      // IMUNM2/3 miss (e.g. EA for a CS-stocked item like 09-29365). Each entry's
      // conversion_factor = units of that UOM per 1 primary/stock UOM, so a source→target
      // factor = Fsource / Ftarget (e.g. EA×4, CS×1 → 4/1 = 4 → 384 EA / 4 = 96 CS).
      if (!factor && itemUoms && itemUoms.length > 0) {
        const fSource = itemUoms.find(u => normalizeUom(u.code, uomMap) === normalizedSource)?.conversion_factor;
        const fTarget = itemUoms.find(u => normalizeUom(u.code, uomMap) === normalizedTarget)?.conversion_factor;
        if (fSource && fTarget && fSource > 0 && fTarget > 0) {
          factor = fSource / fTarget;
        }
      }

      if (!factor) {
        const knownFactor = getKnownConversion(sourceUom, targetUom, uomMap);
        if (knownFactor) factor = knownFactor;
      }

      if (factor && factor > 0) {
        const convertedQty = sourceQty / factor;
        const convertedPrice = Math.round(sourcePrice * factor * 100000) / 100000;
        const factorDisplay = factor === Math.round(factor) ? Math.round(factor) : factor.toFixed(2);
        options.push({
          uom: targetUom,
          qty: Math.round(convertedQty * 100000) / 100000,
          unitPrice: convertedPrice,
          label: `${factorDisplay}${normalizedSource}/${normalizedTarget}`,
          isIdentity: false,
          isCleanQty: isNearInteger(convertedQty),
        });
      } else {
        options.push({ uom: targetUom, qty: null, unitPrice: null, label: null, isIdentity: false, noConversion: true });
      }
    }
    return options;
  }

  const stockingUomOptions = isUomMode ? buildStockingUomOptions() : [];

  const isApplyDisabled = !selectedOption ||
    (selectedOption === 'custom' && (!customQty || !customUom || !customPrice));

  const handleApply = () => {
    if (isApplyDisabled) return;

    let resolution;
    if (selectedOption === 'po') {
      const resolvedUom = isUomMode && !hasContractPricing ? poUom : effectiveContractUom;
      resolution = { origin: mode, type: 'po', quantity: item.poQuantity, uom: resolvedUom, unitPrice: poUnitPrice };
    } else if (selectedOption === 'stock_conversion' && stockConversion) {
      resolution = { origin: mode, type: 'stock_conversion', quantity: stockConversion.qty, uom: stockConversion.uom, unitPrice: stockConversion.unitPrice };
    } else if (selectedOption?.startsWith('stocking_')) {
      const selectedOpt = options.find(o => o.key === selectedOption);
      if (selectedOpt?.resolution) {
        resolution = selectedOpt.resolution;
      } else {
        return;
      }
    } else if (selectedOption === 'contract') {
      if (isUomMode && inferredConversion) {
        resolution = { origin: mode, type: 'contract', quantity: inferredConversion.priceQty, uom: erpUom, unitPrice: item.valUnitPrice };
      } else if (stockResolved && item.priceStockUnitPrice != null) {
        resolution = { origin: mode, type: 'contract', quantity: item.poQuantity, uom: poUom, unitPrice: item.priceStockUnitPrice };
      } else {
        resolution = { origin: mode, type: 'contract', quantity: item.poQuantity, uom: erpUom, unitPrice: item.valUnitPrice };
      }
    } else {
      resolution = { origin: mode, type: 'custom', quantity: parseFloat(customQty), uom: customUom, unitPrice: parseFloat(customPrice) };
    }

    onApply(itemIndex, resolution);
  };

  // Mode-dependent header
  const HeaderIcon = (isUomMode || isManualMode) ? AlertTriangle : XCircle;
  const headerIconBg = (isUomMode || isManualMode) ? 'bg-amber-500/20' : 'bg-red-500/20';
  const headerIconColor = (isUomMode || isManualMode) ? 'text-amber-400' : 'text-red-400';
  const headerTitle = isManualMode ? 'Set Line Price' : isUomMode ? 'Resolve UOM Mismatch' : 'Resolve Price Variance';

  const variancePercent = item.priceVariance != null ? (item.priceVariance * 100).toFixed(1) : '?';

  // Contract option — title and values depend on context
  let contractTitle, contractRightQty, contractRightUom, contractRightPrice;
  if (isUomMode && inferredConversion) {
    contractTitle = `A+ Contract Pricing (Price UOM ${inferredConversion.label})`;
    contractRightQty = inferredConversion.priceQty;
    contractRightUom = erpUom;
    contractRightPrice = item.valUnitPrice;
  } else if (stockResolved) {
    contractTitle = `A+ Contract Pricing (Stock UOM ${item.priceStockUomLabel})`;
    contractRightQty = item.poQuantity;
    contractRightUom = poUom;
    contractRightPrice = item.priceStockUnitPrice;
  } else if (isUomMode) {
    contractTitle = 'Use A+ Contract Pricing';
    contractRightQty = null;  // hide qty in UOM mode if no conversion
    contractRightUom = erpUom;
    contractRightPrice = item.valUnitPrice;
  } else {
    contractTitle = 'Use A+ Contract Pricing';
    contractRightQty = item.poQuantity;
    contractRightUom = erpUom;
    contractRightPrice = item.valUnitPrice;
  }

  // Build selectable options (stocking conversions + contract when available + custom always)
  const options = [];

  // Stocking UOM conversion options (UOM mismatch mode only)
  if (isUomMode && stockingUomOptions.length > 0) {
    for (const opt of stockingUomOptions) {
      if (opt.isIdentity || opt.noConversion) continue;
      options.push({
        key: `stocking_${opt.uom}`,
        title: opt.label ? `Convert to ${opt.uom} (${opt.label})` : `Use ${opt.uom}`,
        tag: 'CONVERTED',
        tagColor: 'bg-sky-500/20 text-sky-400',
        accent: 'border-sky-500',
        accentBg: 'bg-sky-500/5',
        radioColor: 'border-sky-400',
        radioDot: 'bg-sky-400',
        subtitle: opt.isCleanQty === false
          ? `Converted quantity is fractional (${opt.qty})`
          : `Convert PO values to ${opt.uom} stocking unit`,
        rightQty: opt.isCleanQty !== false ? Math.round(opt.qty) : opt.qty,
        rightUom: opt.uom,
        rightPrice: opt.unitPrice,
        resolution: { origin: mode, type: 'stocking_conversion', quantity: opt.isCleanQty !== false ? Math.round(opt.qty) : opt.qty, uom: opt.uom, unitPrice: opt.unitPrice },
      });
    }
  }

  if (hasContractPricing) {
    const contractUomValid = !isUomMode || validStockingUoms.some(su => normalizeUom(su, uomMap) === normalizeUom(erpUom, uomMap));
    if (contractUomValid) {
      options.push({
        key: 'contract',
        title: contractTitle,
        tag: 'CONTRACT',
        tagColor: 'bg-sky-500/20 text-sky-400',
        accent: 'border-sky-500',
        accentBg: 'bg-sky-500/5',
        radioColor: 'border-sky-400',
        radioDot: 'bg-sky-400',
        subtitle: isUomMode ? 'Apply the contract pricing UOM from A+' : 'Apply the contract price from A+',
        rightQty: contractRightQty,
        rightUom: contractRightUom,
        rightPrice: contractRightPrice,
      });
    }
  }

  // Legacy stock conversion — only show if stocking UOM options don't already cover this UOM
  if (stockConversion) {
    const alreadyCovered = options.some(o => o.key === `stocking_${stockConversion.uom}`);
    if (!alreadyCovered) {
      options.push({
        key: 'stock_conversion',
        title: `A+ Stock UOM (${stockConversion.label})`,
        tag: 'CONVERTED',
        tagColor: 'bg-sky-500/20 text-sky-400',
        accent: 'border-sky-500',
        accentBg: 'bg-sky-500/5',
        radioColor: 'border-sky-400',
        radioDot: 'bg-sky-400',
        subtitle: 'Convert PO values to A+ stocking unit of measure',
        rightQty: stockConversion.qty,
        rightUom: stockConversion.uom,
        rightPrice: stockConversion.unitPrice,
      });
    }
  }

  options.push({
    key: 'custom',
    title: 'Enter Custom Values',
    tag: 'MANUAL',
    tagColor: 'bg-emerald-500/20 text-emerald-400',
    accent: 'border-emerald-500',
    accentBg: 'bg-emerald-500/5',
    radioColor: 'border-emerald-400',
    radioDot: 'bg-emerald-400',
    subtitle: 'Specify your own quantity, UOM, and unit price',
  });

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center animate-fadeIn"
    >
      <div
        className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-scaleIn"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${headerIconBg} flex items-center justify-center`}>
                <HeaderIcon className={`w-5 h-5 ${headerIconColor}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{headerTitle}</h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  {isManualMode ? (
                    <>This line was added manually{hasContractPricing ? '' : ' and has no A+ contract price'}. Enter the quantity, UOM, and unit price to use.</>
                  ) : isUomMode ? (
                    hasContractPricing ? (
                      <>The PO specifies <span className="text-amber-400 font-semibold">{poUom}</span> but the A+ contract uses <span className="text-sky-400 font-semibold">{erpUom}</span>. Choose how to proceed.</>
                    ) : (
                      <>The PO specifies <span className="text-amber-400 font-semibold">{poUom}</span> but the A+ stock UOM is <span className="text-sky-400 font-semibold">{stockUom}</span>. No contract pricing exists. Choose how to proceed.</>
                    )
                  ) : (
                    <>The PO price is <span className="text-amber-400 font-semibold">{formatUnitPrice(poUnitPrice)}</span> but the A+ contract price is <span className="text-sky-400 font-semibold">{formatUnitPrice(effectiveContractPrice)}</span> ({variancePercent}% variance). Choose how to proceed.</>
                  )}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Item context bar */}
        <div className="mx-6 mb-4 bg-slate-900/50 rounded-lg px-4 py-2.5 flex items-center gap-3">
          <span className="text-xs font-mono bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
            L{item.poLineNumber || (itemIndex + 1)}
          </span>
          <span className="font-mono font-bold text-white text-sm">{item.selectedItemNumber || item.poItemNumber}</span>
          <span className="text-slate-400 text-sm truncate">{item.selectedDescription || item.poDescription}</span>
        </div>

        <div className="px-6 space-y-3 mb-4">
          {/* PO Values — non-selectable heading when UOM mismatch and PO UOM is not a valid
              stocking UOM, or when contract pricing exists. Selectable only when PO UOM is
              a valid stocking UOM (IMUNM1/2/3) and no contract. Hidden for manual lines. */}
          {isManualMode ? null : ((isUomMode && !poUomIsValidStocking) || (isUomMode && hasContractPricing) ? (
            <div className="rounded-lg border border-slate-600 bg-slate-800 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">PO Values</span>
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                      FROM PO
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Quantity and UOM as stated on the purchase order</p>
                </div>
                {item.poQuantity != null && (
                  <div className="text-right ml-4 flex-shrink-0">
                    <div className="font-mono text-lg font-bold text-white leading-tight">
                      {item.poQuantity} <span className="text-slate-400">{poUom}</span>
                    </div>
                    {poUnitPrice != null && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        @ {formatUnitPrice(poUnitPrice)} = <span className="text-white font-medium bg-slate-700/60 px-1.5 py-0.5 rounded ml-0.5">{formatCurrency(item.poQuantity * poUnitPrice)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setSelectedOption('po')}
              className={`w-full text-left rounded-lg border p-4 transition-all ${
                selectedOption === 'po'
                  ? 'border-amber-500 bg-amber-500/5'
                  : 'border-slate-600 hover:border-slate-500 bg-slate-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 min-w-[1.25rem] min-h-[1.25rem] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedOption === 'po' ? 'border-amber-400' : 'border-slate-500'
                  }`}>
                    {selectedOption === 'po' && <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{isUomMode ? 'Accept PO Values' : 'Accept PO Price'}</span>
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                        FROM PO
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {isUomMode ? 'Submit using the PO quantity, UOM, and price as-is' : 'Accept the purchase order price in the contract UOM'}
                    </p>
                  </div>
                </div>
                {item.poQuantity != null && (
                  <div className="text-right ml-4 flex-shrink-0">
                    <div className="font-mono text-lg font-bold text-white leading-tight">
                      {item.poQuantity} <span className="text-slate-400">{isUomMode && !hasContractPricing ? poUom : effectiveContractUom}</span>
                    </div>
                    {poUnitPrice != null && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        @ {formatUnitPrice(poUnitPrice)} = <span className="text-white font-medium bg-slate-700/60 px-1.5 py-0.5 rounded ml-0.5">{formatCurrency(item.poQuantity * poUnitPrice)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}

          {/* Selectable options (Contract + Custom) */}
          {options.map(opt => {
            const isSelected = selectedOption === opt.key;
            return (
              <div key={opt.key}>
                <button
                  onClick={() => setSelectedOption(opt.key)}
                  className={`w-full text-left rounded-lg border p-4 transition-all ${
                    isSelected
                      ? `${opt.accent} ${opt.accentBg}`
                      : 'border-slate-600 hover:border-slate-500 bg-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Radio circle */}
                      <div className={`w-5 h-5 min-w-[1.25rem] min-h-[1.25rem] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? opt.radioColor : 'border-slate-500'
                      }`}>
                        {isSelected && <div className={`w-2.5 h-2.5 rounded-full ${opt.radioDot}`} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{opt.title}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${opt.tagColor}`}>
                            {opt.tag}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{opt.subtitle}</p>
                      </div>
                    </div>

                    {/* Right side values */}
                    {opt.rightQty != null ? (
                      <div className="text-right ml-4 flex-shrink-0">
                        <div className="font-mono text-lg font-bold text-white leading-tight">
                          {opt.rightQty} <span className="text-slate-400">{opt.rightUom}</span>
                        </div>
                        {opt.rightPrice != null && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            @ {formatUnitPrice(opt.rightPrice)} = <span className="text-white font-medium bg-slate-700/60 px-1.5 py-0.5 rounded ml-0.5">{formatCurrency(opt.rightQty * opt.rightPrice)}</span>
                          </div>
                        )}
                      </div>
                    ) : opt.rightPrice != null ? (
                      <div className="text-right ml-4 flex-shrink-0">
                        <div className="text-xs text-slate-500">
                          <span className="text-slate-400">{opt.rightUom}</span> @ {formatUnitPrice(opt.rightPrice)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </button>

                {/* Custom form (expanded when custom is selected) */}
                {opt.key === 'custom' && isSelected && (
                  <div className="animate-fadeSlideDown mt-3">
                    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Qty</label>
                          <input
                            type="number"
                            value={customQty}
                            onChange={e => setCustomQty(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">UOM</label>
                          <select
                            value={customUom}
                            onChange={e => handleCustomUomChange(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white font-mono text-sm focus:border-emerald-500 focus:outline-none appearance-none"
                          >
                            {uomOptions.map(code => {
                              const meta = itemUoms?.find(u => u.code === code);
                              const label = meta?.description ? `${code} — ${meta.description}` : code;
                              return <option key={code} value={code}>{label}</option>;
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Unit Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                            <input
                              type="text"
                              value={customPrice}
                              onChange={e => setCustomPrice(e.target.value)}
                              className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-7 pr-3 py-2 text-white font-mono text-sm focus:border-emerald-500 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                      {customTotal != null && customTotal > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700 text-sm text-white text-right">
                          {customQty} {customUom} @ {formatUnitPrice(parseFloat(customPrice))} = <span className="font-medium">{formatCurrency(customTotal)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 transition-all text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={isApplyDisabled}
            className={`px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
              isApplyDisabled
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 hover:shadow-lg hover:shadow-emerald-500/20'
            }`}
          >
            Apply Resolution
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
